import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calculator, Calendar, Info, ExternalLink } from 'lucide-react';

export default function Graviditet() {
  const [lastPeriod, setLastPeriod] = useState('');
  const [result, setResult] = useState(null);

  const calculatePregnancy = (e) => {
    e.preventDefault();
    if (!lastPeriod) return;

    const lmpDate = new Date(lastPeriod);
    const today = new Date();
    
    // Calculate weeks and days
    const diffTime = Math.abs(today - lmpDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;

    // Calculate due date (LMP + 280 days)
    const dueDate = new Date(lmpDate);
    dueDate.setDate(dueDate.getDate() + 280);

    setResult({
      weeks,
      days,
      dueDate: dueDate.toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })
    });
  };

  return (
    <main className="pt-32 pb-24">
      <Helmet>
        <title>Graviditetsundersøgelse & Jordemoder | Hellerup Lægehus</title>
        <meta name="description" content="Tillykke med graviditeten! Læs om de rutinemæssige graviditetsundersøgelser i uge 8, 25 og 32 hos vores jordemoder. Beregn din terminsdato her." />
        <meta property="og:title" content="Graviditetsundersøgelse & Jordemoder | Hellerup Lægehus" />
        <meta property="og:description" content="Tillykke med graviditeten! Læs om de rutinemæssige graviditetsundersøgelser i uge 8, 25 og 32 hos vores jordemoder. Beregn din terminsdato her." />
        <html lang="da-DK" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-[1px] w-12 bg-[#4b717a]" />
            <span className="text-[#4b717a] text-xs tracking-[0.4em] uppercase font-bold">Information</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-sans font-medium text-black mb-8 leading-tight tracking-tight">
            Gravide
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="prose prose-slate prose-lg max-w-none
              prose-headings:font-sans prose-headings:font-medium prose-headings:text-black prose-headings:mt-12 prose-headings:mb-6 prose-headings:leading-tight prose-headings:tracking-tight
              prose-p:text-slate-600 prose-p:leading-relaxed
              prose-a:text-[#4b717a] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-slate-900">
              
              <p className="text-xl text-slate-700 font-medium mb-8">
                Tillykke. Vi glæder og til at følge dig igennem graviditeten og se barnet når den tid kommer.
              </p>

              <p>
                Du skal til rutinemæssig lægeundersøgelse i klinikken i uge 8, 25 og 32. som varetages af vores jordemoder.
              </p>

              <p>
                Hvis du er i tvivl om du er gravid og om du ønsker at afbryde graviditeten, anbefales du en tid hos jordemoderen.
              </p>

              <div className="my-12 bg-slate-50 p-8 border border-slate-100">
                <div className="flex items-center gap-3 mb-6">
                  <Calculator className="text-[#4b717a]" size={24} />
                  <h2 className="text-2xl font-sans font-medium m-0">Beregn hvor langt du er</h2>
                </div>
                
                <form onSubmit={calculatePregnancy} className="space-y-6">
                  <div>
                    <label htmlFor="lmp" className="block text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                      Første dag i sidste menstruation
                    </label>
                    <div className="flex gap-4">
                      <input
                        type="date"
                        id="lmp"
                        value={lastPeriod}
                        onChange={(e) => setLastPeriod(e.target.value)}
                        className="flex-grow bg-white border border-slate-200 px-4 py-3 focus:outline-none focus:border-[#4b717a] transition-colors"
                        required
                      />
                      <button
                        type="submit"
                        className="bg-[#4b717a] text-white px-8 py-3 font-bold tracking-widest uppercase text-xs hover:bg-[#3d5c64] transition-colors"
                      >
                        Beregn
                      </button>
                    </div>
                  </div>
                </form>

                {result && (
                  <div className="mt-8 p-6 bg-white border border-slate-100 shadow-sm animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Status</span>
                        <p className="text-2xl text-black font-sans font-medium">
                          {result.weeks} uger og {result.days} dage
                        </p>
                      </div>
                      <div>
                        <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Forventet termin</span>
                        <p className="text-2xl text-black font-sans font-medium">
                          {result.dueDate}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <h2 className="text-3xl font-sans font-medium text-black mt-12 mb-6">Nakkefoldsskanning</h2>
              <p>
                Alle gravide får tilbudt en vurdering af risikoen for Downs syndrom (trisomi 21) hos fosteret. Undersøgelsen består af en blodprøve fra den gravide (doubletest) og en skanning af fosteret (nakkefoldsskanning). Tilsammen kan disse to undersøgelser, når de kombineres med din alder, finde omkring 90 % af fostrene med Downs syndrom.
              </p>
              <p>
                Hvis du ønsker undersøgelserne, skal du selv booke tid til doubletest/nakkefoldsscanning på det hospital, hvor du skal føde. Scanningen sker fra uge 11+0 til uge 13+6.
              </p>
              
              <div className="mt-8">
                <a 
                  href="https://www.regionh.dk/selvbetjening/Sider/Bestil-tid-til-nakkefoldsscanning.aspx" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#4b717a] text-white px-8 py-4 font-bold tracking-widest uppercase text-xs hover:bg-[#3d5c64] transition-colors"
                >
                  Book tid til scanning her
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-slate-50 p-8 border border-slate-100">
              <h3 className="text-xl font-sans font-medium mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-[#4b717a]" />
                Vigtige datoer
              </h3>
              <ul className="space-y-4 text-sm text-slate-600">
                <li className="flex gap-3">
                  <span className="font-bold text-[#4b717a] whitespace-nowrap">Uge 8:</span>
                  <span>Første lægeundersøgelse hos jordemoderen.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#4b717a] whitespace-nowrap">Uge 11-14:</span>
                  <span>Nakkefoldsskanning (skal bookes selv).</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#4b717a] whitespace-nowrap">Uge 25:</span>
                  <span>Anden lægeundersøgelse hos jordemoderen.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#4b717a] whitespace-nowrap">Uge 32:</span>
                  <span>Tredje lægeundersøgelse hos jordemoderen.</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#4b717a] p-8 text-white">
              <h3 className="text-xl font-sans font-medium mb-4 flex items-center gap-2">
                <Info size={20} />
                Brug for hjælp?
              </h3>
              <p className="text-sm text-slate-100 mb-6 leading-relaxed">
                Er du i tvivl om noget vedrørende din graviditet, er du altid velkommen til at kontakte os.
              </p>
              <a 
                href="tel:70602202" 
                className="block text-center bg-white text-[#4b717a] py-3 font-bold tracking-widest uppercase text-xs hover:bg-slate-50 transition-colors"
              >
                Ring 70 60 22 02
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
